import { useCallback, useEffect, useRef, useState } from "react";
import { api, fmtDateTime, parseApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerFooter } from "@/components/ui/drawer";
import { AlertTriangle, Camera, Check, ChevronRight, Clock, ListChecks, Send, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { compressImageToDataUrl, fileToCompressedDataUrl } from "@/lib/image";

const STATUS = {
  open: { l: "Mở", c: "bg-stone-200 text-stone-800" },
  pending: { l: "Mở", c: "bg-stone-200 text-stone-800" },
  in_progress: { l: "Đang làm", c: "bg-blue-100 text-blue-900" },
  awaiting_approval: { l: "Chờ duyệt", c: "bg-amber-100 text-amber-900" },
  submitted: { l: "Chờ duyệt", c: "bg-amber-100 text-amber-900" },
  done: { l: "Xong", c: "bg-emerald-100 text-emerald-900" },
  approved: { l: "Đã duyệt", c: "bg-emerald-100 text-emerald-900" },
  rejected: { l: "Trả lại", c: "bg-red-100 text-red-900" },
  failed: { l: "Lỗi", c: "bg-red-100 text-red-900" },
  skipped: { l: "Bỏ qua", c: "bg-stone-100 text-stone-500" },
};

// Statuses from which a daily item can still be (re)completed by the worker.
const DAILY_ACTIONABLE = new Set(["open", "pending", "in_progress", "rejected"]);

export default function StaffTasks() {
  const [tasks, setTasks] = useState([]);
  const [legacyTasks, setLegacyTasks] = useState([]);
  const [active, setActive] = useState(null);
  const [note, setNote] = useState("");
  const [filter, setFilter] = useState("");
  const [photo, setPhoto] = useState(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const streamRef = useRef(null);
  const videoRef = useRef(null);

  const load = useCallback(() => {
    const today = new Date().toISOString().slice(0, 10);
    api.get(`/hrm/daily-work/my-tasks?work_date=${today}`).then((r) => setTasks(r.data));
    const url = filter ? `/tasks?mine=true&status=${filter}` : `/tasks?mine=true`;
    api.get(url).then((r) => setLegacyTasks(r.data));
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setCameraOpen(false);
  }, []);

  // Stop any live camera track when the component unmounts.
  useEffect(() => () => stopCamera(), [stopCamera]);

  // Attach the live stream once the <video> element is mounted.
  useEffect(() => {
    if (cameraOpen && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play().catch(() => {});
    }
  }, [cameraOpen]);

  const openTask = (task) => {
    stopCamera();
    setPhoto(task.photo_url || null);
    setActive(task);
    setNote(task.completion_note || "");
  };

  const closeDrawer = () => {
    stopCamera();
    setActive(null);
    setNote("");
    setPhoto(null);
  };

  const startCamera = async () => {
    try {
      streamRef.current = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      setCameraOpen(true);
    } catch {
      toast.error("Không mở được camera. Hãy thử tải ảnh lên.");
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current) return;
    setPhoto(compressImageToDataUrl(videoRef.current));
    stopCamera();
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      setPhoto(await fileToCompressedDataUrl(file));
    } catch {
      toast.error("Không đọc được ảnh");
    }
  };

  const transition = async (action) => {
    try {
      if (active.source === "daily") {
        if (action !== "fail" && active.requires_photo && !photo) {
          toast.error("Cần chụp ảnh để hoàn thành");
          return;
        }
        await api.post(`/hrm/daily-work/items/${active.id}/complete`, {
          status: action === "fail" ? "failed" : "done",
          note,
          photo_url: photo || undefined,
          version: active.version,
        });
        toast.success(action !== "fail" && active.requires_photo ? "Đã gửi duyệt 🎉" : "Cập nhật rồi");
      } else {
        await api.post(`/tasks/${active.id}/transition`, { action, note });
        toast.success(action === "submit" ? "Đã gửi duyệt 🎉" : "Cập nhật rồi");
      }
      closeDrawer(); load();
    } catch (e) { toast.error(parseApiError(e, "Thất bại").message); }
  };

  const reportIncident = async () => {
    try {
      await api.post("/hrm/incidents", {
        assignment_item_id: active.id,
        severity: "medium",
        description: note || "Báo lỗi công việc hằng ngày",
      });
      toast.success("Đã báo lỗi");
      closeDrawer(); load();
    } catch (e) { toast.error(parseApiError(e, "Thất bại").message); }
  };

  return (
    <div className="p-4 space-y-3" data-testid="staff-tasks">
      <div className="flex items-center gap-2">
        <h2 className="font-serif text-2xl font-bold flex-1">Việc của tôi</h2>
        <ListChecks className="w-5 h-5 text-emerald-800" />
      </div>

      <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
        {[["", "Tất cả"], ["open", "Mở"], ["in_progress", "Đang làm"], ["submitted", "Chờ duyệt"], ["approved", "Xong"]].map(([k, l]) => (
          <button
            key={k} onClick={() => setFilter(k)}
            className={`whitespace-nowrap text-xs px-3 py-1.5 rounded-sm font-bold ${filter === k ? "bg-emerald-900 text-white" : "bg-white border border-stone-200 text-stone-700"}`}
            data-testid={`filter-${k || "all"}`}
          >{l}</button>
        ))}
      </div>

      <div className="space-y-2">
        {tasks.map((t) => {
          const st = STATUS[t.status] || { l: t.status, c: "bg-stone-100" };
          return (
            <button
              key={t.id}
              onClick={() => openTask({ ...t, source: "daily", title: t.task_name, description: t.expected_result })}
              className="w-full text-left bg-white border border-stone-200 rounded-sm p-3 active:bg-stone-50 transition-colors"
              data-testid={`task-card-${t.id}`}
            >
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <h3 className="font-semibold text-stone-900 leading-tight flex items-center gap-1.5">
                      {t.requires_photo && <Camera className="w-3.5 h-3.5 text-amber-700 shrink-0" />}
                      {t.title}
                    </h3>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-sm font-semibold shrink-0 ${st.c}`}>{st.l}</span>
                  </div>
                  {t.expected_result && <p className="text-xs text-stone-600 line-clamp-2 mb-1.5">{t.expected_result}</p>}
                  <div className="flex items-center justify-between text-xs">
                    <span className="flex items-center gap-1 text-stone-500"><Clock className="w-3 h-3" />{t.shift_name || "Ca"} · {t.branch_name || "Cơ sở"}</span>
                    <span className="text-[10px] px-1 py-0.5 bg-emerald-100 text-emerald-900 font-bold rounded-sm">{t.traffic_signal}</span>
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-stone-400 shrink-0 mt-1" />
              </div>
            </button>
          );
        })}
        {tasks.length === 0 && <div className="text-center text-stone-400 py-8 text-sm">Bạn không có daily task nào.</div>}
      </div>

      <div className="pt-3">
        <h3 className="text-sm font-bold text-stone-700 mb-2">Việc đột xuất</h3>
        <div className="space-y-2">
          {legacyTasks.map((t) => {
            const st = STATUS[t.status] || { l: t.status, c: "bg-stone-100" };
            return (
              <button key={t.id} onClick={() => openTask({ ...t, source: "legacy" })} className="w-full text-left bg-white border border-stone-200 rounded-sm p-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold text-stone-900">{t.title}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-sm font-semibold shrink-0 ${st.c}`}>{st.l}</span>
                </div>
                {t.due_at && <div className="text-xs text-stone-500 mt-1">{fmtDateTime(t.due_at)}</div>}
              </button>
            );
          })}
        </div>
      </div>

      <Drawer open={!!active} onOpenChange={(o) => !o && closeDrawer()}>
        <DrawerContent className="max-h-[90vh]">
          <DrawerHeader className="text-left">
            <DrawerTitle className="font-serif text-xl">{active?.title}</DrawerTitle>
            {active?.description && <p className="text-sm text-stone-600">{active.description}</p>}
          </DrawerHeader>
          {active && (
            <div className="px-4 space-y-3 overflow-y-auto">
              {active.sop_steps?.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-emerald-800 font-bold mb-2">Các bước SOP</div>
                  <ul className="space-y-1.5">
                    {(active.sop_steps || []).map((s, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <div className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-900 text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5">{i+1}</div>
                        <span>{s}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {active.source === "daily" && active.rejection_reason && active.status === "rejected" && (
                <div className="rounded-sm border border-red-200 bg-red-50 p-2 text-sm text-red-800">
                  <span className="font-bold">Bị trả lại: </span>{active.rejection_reason}
                </div>
              )}
              {active.source === "daily" && active.requires_photo && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-amber-800 font-bold mb-1">Ảnh xác minh (bắt buộc)</div>
                  {cameraOpen ? (
                    <div className="space-y-2">
                      <video ref={videoRef} playsInline muted className="w-full rounded-sm bg-black aspect-video object-cover" />
                      <div className="grid grid-cols-2 gap-2">
                        <Button type="button" onClick={capturePhoto} className="h-11 bg-amber-700 hover:bg-amber-800 rounded-sm" data-testid="btn-capture">
                          <Camera className="w-4 h-4 mr-2" />Chụp
                        </Button>
                        <Button type="button" variant="outline" onClick={stopCamera} className="h-11 rounded-sm">
                          <X className="w-4 h-4 mr-2" />Huỷ
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3">
                      {photo && (
                        <div className="relative shrink-0">
                          <img src={photo} alt="Ảnh xác minh" className="w-20 h-20 rounded-md object-cover border border-stone-200" data-testid="task-photo-preview" />
                          <button type="button" onClick={() => setPhoto(null)} className="absolute -top-1.5 -right-1.5 bg-white border border-stone-300 rounded-full p-0.5" aria-label="Xoá ảnh">
                            <X className="w-3 h-3 text-stone-600" />
                          </button>
                        </div>
                      )}
                      <div className="flex flex-col gap-2 flex-1">
                        <Button type="button" onClick={startCamera} className="h-11 bg-emerald-800 hover:bg-emerald-900 rounded-sm" data-testid="btn-camera">
                          <Camera className="w-4 h-4 mr-2" />Chụp ảnh
                        </Button>
                        <label className="h-11 inline-flex items-center justify-center rounded-sm border border-stone-300 text-sm font-medium cursor-pointer active:bg-stone-50" data-testid="btn-upload">
                          <Upload className="w-4 h-4 mr-2" />Tải ảnh lên
                          <input type="file" accept="image/*" capture="environment" className="hidden" onChange={handleUpload} />
                        </label>
                      </div>
                    </div>
                  )}
                </div>
              )}
              <div>
                <div className="text-xs uppercase tracking-wider text-stone-500 font-bold mb-1">Ghi chú / Bằng chứng</div>
                <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="Ghi chú khi hoàn thành..." className="rounded-sm" data-testid="task-note" />
              </div>
            </div>
          )}
          <DrawerFooter className="grid grid-cols-2 gap-2">
            {active?.source === "daily" && DAILY_ACTIONABLE.has(active?.status) && (
              <>
                <Button onClick={() => transition("done")} className="h-12 bg-emerald-800 hover:bg-emerald-900 rounded-sm" data-testid="btn-daily-done">
                  <Check className="w-4 h-4 mr-2" />{active?.requires_photo ? "Gửi duyệt" : "Đánh dấu xong"}
                </Button>
                <Button onClick={reportIncident} variant="outline" className="h-12 rounded-sm" data-testid="btn-daily-incident">
                  <AlertTriangle className="w-4 h-4 mr-2" />Báo lỗi
                </Button>
              </>
            )}
            {active?.source !== "daily" && active?.status === "open" && (
              <Button onClick={() => transition("start")} className="h-12 bg-blue-700 hover:bg-blue-800 rounded-sm col-span-2" data-testid="btn-start">
                Bắt đầu làm việc
              </Button>
            )}
            {active?.source !== "daily" && active?.status === "in_progress" && (
              <Button onClick={() => transition("submit")} className="h-12 bg-emerald-800 hover:bg-emerald-900 rounded-sm col-span-2" data-testid="btn-submit">
                <Send className="w-4 h-4 mr-2" />Gửi duyệt
              </Button>
            )}
            {active?.source !== "daily" && active?.status === "rejected" && (
              <Button onClick={() => transition("start")} className="h-12 bg-blue-700 hover:bg-blue-800 rounded-sm col-span-2">Làm lại</Button>
            )}
            <Button variant="outline" onClick={closeDrawer} className="rounded-sm col-span-2 h-10">Đóng</Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </div>
  );
}
